import type {
  GeometryLineageResponse,
  GeometryStatus,
} from '@/api/generated/types.gen';

// A lineage version is eligible to be the displayed / mesh tip when it is ready
// (`idle`) or on its way to ready (`diagnosing` / `repairing`). Terminal
// not-ready states are excluded: `error` (failed diagnosis, or a no-op/rejected
// derived version — a dead branch that is often artifact-less, so it can neither
// preview nor mesh) and `awaiting_approval` (an unapproved CAD-simplify proposal,
// not the active version). A positive allowlist is deliberate: an unknown future
// status is treated as ineligible and falls back to the backend's own tip.
const TIP_ELIGIBLE_STATUSES: ReadonlySet<GeometryStatus> = new Set([
  'idle',
  'diagnosing',
  'repairing',
]);

/**
 * The lineage tip the MeshGen workspace displays and meshes by default: the
 * newest eligible version. `lineage.versions` is ordered oldest-first (backend
 * `ORDER BY created_at ASC`), so we scan from the end and return the first
 * version whose status is `TIP_ELIGIBLE_STATUSES`.
 *
 * This deliberately differs from the backend's `current_geometry_id`, which is
 * the newest *IDLE* version and falls back to the lineage ROOT whenever the
 * newest version is non-IDLE. Crucially, `current_geometry_id` also skips a
 * still-`diagnosing` revision — so seeding `activeGeometryId` / defaulting the
 * mesh target off it pointed Generate Mesh at v1 (the root) while the user was on
 * a freshly-uploaded, still-diagnosing v2 (the "meshes the earliest version"
 * bug). We keep `diagnosing`/`repairing` tips (they become ready, and the
 * Generate button gates on their status until then) while still skipping the
 * `error`/`awaiting_approval` dead branches that `current_geometry_id` skips.
 * Falls back to `current_geometry_id` only when there is no eligible version
 * (empty list, or every version is errored / an unapproved candidate).
 */
export function lineageTipId(
  lineage: GeometryLineageResponse | undefined | null,
): string | null {
  const versions = lineage?.versions;
  if (versions?.length) {
    for (let i = versions.length - 1; i >= 0; i--) {
      if (TIP_ELIGIBLE_STATUSES.has(versions[i].status)) return versions[i].id;
    }
  }
  return lineage?.current_geometry_id ?? null;
}
